import React, { useCallback } from "react";
import { Button } from "@jsq/ds/core";
import { actionButton } from "./styles_vanilla.css";

/**
 * The action type for a table row.
 * - "review": Show "Review" button when there are payment batches requiring treasury review
 * - "pay": Show "Pay" button when remaining to pay > 0 and no batches need review
 * - null: No action button should be shown
 */
export type ActionType = "review" | "pay" | null;

export interface ActionButtonProps {
  /** The action type to display */
  action: ActionType;
  /** Optional click handler for the action button */
  onClick?: () => void;
}

/**
 * ActionButton component for table action columns.
 * Renders a "Review" or "Pay" button based on the action type.
 * If action is null, nothing is rendered.
 */
export const ActionButton: React.FC<ActionButtonProps> = ({ action, onClick }) => {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Stop propagation to prevent row click handler from firing
      e.stopPropagation();
      onClick?.();
    },
    [onClick],
  );

  if (!action) {
    return null;
  }

  const buttonText = action === "review" ? "Review" : "Pay";

  return (
    <Button
      data-testid={`action-button-${action}`}
      className={actionButton}
      variant="neutral"
      onClick={handleClick}
    >
      {buttonText}
    </Button>
  );
};

export default ActionButton;
